import React from 'react';
import ReactDOM from 'react-dom';
import { BrandProvider } from '../themes/BrandProvider';
import { Layout } from '../Layout/Layout';
import { GlobalConfig, GlobalProvider } from '../hooks/useGlobal';
import { PlacesAutocompleteProvider } from '../hooks/usePlacesAutoComplete';
import { AnalyticsClient, MapProvider, WidgetErrorFallback } from 'rwl-ui';
import { WidgetType } from 'rwl-shared/dist/models/analytics';
import {
    ConfigFetcher,
    ContentListsProvider,
    ContentFetcher,
} from '../fetchers';
import { MountedQueryClientProvider } from 'rwl-shared/dist/hooks';
import {
    AnalyticsProvider,
    TriggerLoadEvent,
} from '../hooks/useAnalyticsProviders';
import { BreakpointProvider, ContainerWidthProvider } from '../providers';
import { MapProvider as NeighborhoodMapProvider } from '../hooks/MapProvider';
import { ParentPageIntersectionProvider } from '../hooks/intersectionObservers/useParentPageIntersection';
import { JobProvider } from '../fetchers/JobFetcher';
import { ReviewProvider } from '../fetchers/ReviewFetcher';
import {
    SelectedPostCodeProvider,
    SelectedPostCodeSync,
} from '../hooks/useSelectedPostCode';
import { SlideOutTab } from '../components';
import * as Sentry from '@sentry/react';

type WidgetProps = GlobalConfig & {
    pluginPropertyId?: string;
    analytics?: {
        client?: AnalyticsClient;
    };
    widgetType: WidgetType;
};

export const Widget = (props: WidgetProps): JSX.Element => {
    const { pluginPropertyId, analytics, widgetType, ...globalConfig } = props;

    const mainContent = (
        <>
            <TriggerLoadEvent />
            <MountedQueryClientProvider>
                <ContentListsProvider>
                    <SelectedPostCodeProvider>
                        <ConfigFetcher />
                        <JobProvider>
                            <ReviewProvider>
                                <BrandProvider>
                                    <MapProvider>
                                        <PlacesAutocompleteProvider>
                                            <NeighborhoodMapProvider>
                                                {/* ContentFetcher and SelectedPostCodeSync must be inside PlacesAutocompleteProvider */}
                                                <ContentFetcher />
                                                <SelectedPostCodeSync />
                                                <ParentPageIntersectionProvider>
                                                    <Layout />
                                                </ParentPageIntersectionProvider>
                                            </NeighborhoodMapProvider>
                                        </PlacesAutocompleteProvider>
                                    </MapProvider>
                                </BrandProvider>
                            </ReviewProvider>
                        </JobProvider>
                    </SelectedPostCodeProvider>
                </ContentListsProvider>
            </MountedQueryClientProvider>
        </>
    );

    // If bumpOutTest is enabled and bumpOutTest object exists, wrap with SlideOutTab
    if (globalConfig.bumpOutTestEnabled && globalConfig.bumpOutTest) {
        return (
            <Sentry.ErrorBoundary fallback={<WidgetErrorFallback />}>
                <GlobalProvider {...globalConfig}>
                    <AnalyticsProvider
                        pluginPropertyId={pluginPropertyId}
                        client={analytics?.client}
                        widgetType={widgetType}
                    >
                        <ContainerWidthProvider>
                            <BreakpointProvider>
                                <SlideOutTab>{mainContent}</SlideOutTab>
                            </BreakpointProvider>
                        </ContainerWidthProvider>
                    </AnalyticsProvider>
                </GlobalProvider>
            </Sentry.ErrorBoundary>
        );
    }

    // Return content without SlideOutTab if not enabled
    return (
        <Sentry.ErrorBoundary fallback={<WidgetErrorFallback />}>
            <GlobalProvider {...globalConfig}>
                <AnalyticsProvider
                    pluginPropertyId={pluginPropertyId}
                    client={analytics?.client}
                    widgetType={widgetType}
                >
                    <ContainerWidthProvider>
                        <BreakpointProvider>{mainContent}</BreakpointProvider>
                    </ContainerWidthProvider>
                </AnalyticsProvider>
            </GlobalProvider>
        </Sentry.ErrorBoundary>
    );
};

/**
 * Mount the widget into a DOM element using the same React instance the widget
 * resolved at import time. This ensures the React used for rendering and the
 * React used inside hooks are the same instance, avoiding dual-React crashes.
 */
export const mount = (target: HTMLElement, props: WidgetProps): void => {
    ReactDOM.render(<Widget {...props} />, target);
};
